import { html, nothing } from 'lit';
import { customElement, query, state } from 'lit/decorators.js';

import { answerDataGrant } from '../../../../../helpers/company-data.js';
import NSJobComponent from '../../../job.js';
import JobSchema from '../../../../../models/job-schema.js';
import type Company from '../../../../../models/company.js';
import type { DataGrant, DataSet } from '../../../../../models/company-data.js';
import type { DataSharePicker } from '../../../../../views/data/data-share-picker.js';

import '../../../../../views/data/data-share-picker.js';
import '../../../../../ns-elements/ns-job-layout.js';
import '@awesome.me/webawesome/dist/components/button/button.js';
import definition from '@job-schemas/company/data-access/answer.json';
import { jobSchema } from '../../../job-schema.js';

// A connected company asked for this company's data: an admin picks what to share, or declines
@customElement(NSCompanyDataAccessAnswer.is)
export default class NSCompanyDataAccessAnswer extends NSJobComponent {
  static get is() {
    return 'ns-company-data-access-answer';
  }

  @state()
  private __grant?: DataGrant;

  @state()
  private __requester?: Company;

  @state()
  private __setName = '';

  @query('data-share-picker')
  private __picker?: DataSharePicker;

  static get schema(): JobSchema {
    return jobSchema(definition, { outcomes: { answered: { type: 'hidden' } } });
  }

  async connectedCallback() {
    try {
      await super.connectedCallback();
      if (!this._db) throw new Error('Unable to find db service');
      const grantId = this.job.inputValues.GRANT_ID;
      if (typeof grantId !== 'string' || !grantId) throw new Error('Missing required grant ID');
      this.__grant = await this._db.getById<DataGrant>('dataGrant', grantId);
      if (!this.__grant) throw new Error('Unable to find the request');
      this.__requester = await this._db.getById<Company>('company', this.__grant.granteeCompanyId);
      if (this.__grant.dataSetId) {
        this.__setName = (await this._db.getById<DataSet>('dataSet', this.__grant.dataSetId))?.name ?? 'a data set';
      }
    } catch (err: unknown) {
      this.failJob(err, 'Unexpected error occurred while loading the request');
    }
  }

  private async __answer(decision: 'accept' | 'decline') {
    if (!this.__grant) return;
    const choice = this.__grant.replacesGrantId || decision === 'decline' ? {} : this.__picker?.choice;
    if (!choice) {
      this.fatalError('Choose what to share, or decline the request.');
      return;
    }
    try {
      this.loading(decision === 'accept' ? 'Sharing' : 'Declining');
      await answerDataGrant(this._auth?.token, { grantId: this.__grant.id, decision, ...choice });
      this.loaded();
      // The lambda closes this job with the workflow, so the card only has to go
      this._jobComplete();
    } catch (err) {
      this.loaded();
      this.showError(err, 'Unable to answer the request.');
    }
  }

  render() {
    const grant = this.__grant;
    const who = this.__requester?.name ?? 'A connected company';
    const admin = this._directory?.can('DATA_SHARING_MANAGE') ?? false;
    const level = grant?.level === 'CONTRIBUTE' ? 'to read and add records' : 'to read';
    const headline = grant?.replacesGrantId
      ? `${who} would like ${grant.level === 'CONTRIBUTE' ? 'to add records to' : 'all of'} ${this.__setName}.`
      : `${who} would like access to your data, ${level}.`;
    if (grant && grant.status !== 'REQUESTED') {
      return html`
        <ns-job-layout><p slot="intro">This request has already been answered or withdrawn.</p></ns-job-layout>
      `;
    }
    return html`
      <ns-job-layout>
        <p slot="intro">${headline}</p>
        <div class="job-panel">
          <h3>What they asked for</h3>
          <p class="note">${grant?.note || 'No note given.'}</p>
          ${admin && grant && !grant.replacesGrantId
            ? html`
                <data-share-picker id-prefix="answer"></data-share-picker>
              `
            : nothing}
          ${admin
            ? nothing
            : html`
                <p>Only a company admin can answer this.</p>
              `}
        </div>
      </ns-job-layout>
    `;
  }

  renderActions() {
    const grant = this.__grant;
    if (grant && grant.status !== 'REQUESTED') return nothing;
    const admin = this._directory?.can('DATA_SHARING_MANAGE') ?? false;
    return html`
      <wa-button
        variant="brand"
        appearance="outlined"
        type="button"
        ?disabled=${!admin}
        @click=${() => this.__answer('decline')}
      >
        Decline
      </wa-button>
      <wa-button variant="brand" type="button" ?disabled=${!admin} @click=${() => this.__answer('accept')}>
        ${grant?.replacesGrantId ? 'Agree' : 'Share'}
      </wa-button>
    `;
  }
}
